import React, { useState, useEffect, useMemo } from "react";
import { ChevronLeft, ChevronRight, MapPin, Clock } from "lucide-react";
import { toast } from "sonner";
import { Nav, Footer } from "./Nav";
import { api } from "../api";
import { useAuth } from "../context/AuthContext";
import { Seo } from "./Seo";

const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const TYPE_COLOR = { session: "#3E8E41", course: "#C7921E", retreat: "#B0563A" };
const pad = (n) => String(n).padStart(2, "0");

export function CalendarPage() {
  const { user, openAuth } = useAuth();
  const [cursor, setCursor] = useState(new Date(2026, 5, 1)); // June 2026 (current)
  const [events, setEvents] = useState([]);

  const year = cursor.getFullYear();
  const month = cursor.getMonth();
  const daysInMonth = new Date(year, month + 1, 0).getDate();

  useEffect(() => {
    const start = `${year}-${pad(month + 1)}-01`;
    const end = `${year}-${pad(month + 1)}-${pad(daysInMonth)}`;
    api.get(`/calendar?start=${start}&end=${end}`).then((r) => setEvents(r.data)).catch(() => setEvents([]));
  }, [year, month, daysInMonth]);

  const byDay = useMemo(() => {
    const map = {};
    for (let d = 1; d <= daysInMonth; d++) map[d] = [];
    events.forEach((ev) => {
      const s = new Date(ev.date + "T00:00:00");
      const e = new Date((ev.end_date || ev.date) + "T00:00:00");
      for (let dt = new Date(s); dt <= e; dt.setDate(dt.getDate() + 1)) {
        if (dt.getFullYear() === year && dt.getMonth() === month) map[dt.getDate()]?.push(ev);
      }
    });
    return map;
  }, [events, year, month, daysInMonth]);

  const blocks = useMemo(
    () => events.filter((e) => e.type !== "session").sort((a, b) => a.date.localeCompare(b.date)),
    [events]
  );

  const startOffset = (new Date(year, month, 1).getDay() + 6) % 7; // Mon = 0
  const cells = [];
  for (let i = 0; i < startOffset; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  const book = async (ev) => {
    if (!ev.item_id) return;
    if (!user) { openAuth("login"); return; }
    try {
      const { data } = await api.post("/checkout/session", { item_id: ev.item_id, origin_url: window.location.origin });
      if (data.url) window.location.href = data.url;
    } catch { toast.error("Could not start checkout. Please try again."); }
  };

  const move = (delta) => setCursor(new Date(year, month + delta, 1));

  return (
    <>
      <Nav />
      <Seo title="Training & Therapy Calendar" description="Book your place on upcoming Ely Wellbeing NLP trainings, therapy sessions and retreats." />
      <section className="hero" style={{ padding: "9rem 0 2rem" }} data-testid="calendar-page">
        <div className="wrap">
          <span className="badge-gold">BOOK YOUR PLACE</span>
          <h1 style={{ marginTop: "1rem", fontSize: "clamp(2rem,4vw,3.4rem)" }}>Calendar</h1>
          <p className="lead">Daily live sessions, practitioner intensives and our Zanzibar retreats — reserve your place in a click.</p>
        </div>
      </section>

      <section className="sec pad" style={{ paddingTop: 0 }}>
        <div className="wrap">
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "1.2rem", flexWrap: "wrap", gap: ".6rem" }}>
            <h2 style={{ color: "#fff", margin: 0 }}>{MONTHS[month]} {year}</h2>
            <div style={{ display: "flex", gap: ".5rem", alignItems: "center" }}>
              <button className="icon-round" data-testid="cal-prev" onClick={() => move(-1)} aria-label="Previous month"><ChevronLeft size={18} /></button>
              <button className="icon-round" data-testid="cal-next" onClick={() => move(1)} aria-label="Next month"><ChevronRight size={18} /></button>
            </div>
          </div>

          <div style={{ display: "flex", gap: "1.2rem", flexWrap: "wrap", marginBottom: "1rem", fontSize: ".8rem" }}>
            {Object.entries({ session: "Live session (£9)", course: "Practitioner intensive", retreat: "Zanzibar retreat" }).map(([k, label]) => (
              <span key={k} style={{ display: "inline-flex", alignItems: "center", gap: ".4rem", color: "var(--muted)" }}>
                <span style={{ width: 12, height: 12, borderRadius: 3, background: TYPE_COLOR[k], display: "inline-block" }} /> {label}
              </span>
            ))}
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(7,1fr)", gap: 6 }}>
            {WEEKDAYS.map((w) => (
              <div key={w} style={{ textAlign: "center", color: "var(--muted)", fontSize: ".75rem", fontWeight: 700, padding: ".3rem 0" }}>{w}</div>
            ))}
            {cells.map((d, i) => (
              <div key={i} style={{
                minHeight: 108, borderRadius: 10, padding: 6,
                background: d ? "var(--panel)" : "transparent",
                border: d ? "1px solid var(--line)" : "none",
              }}>
                {d && <div style={{ fontSize: ".75rem", color: "var(--muted)", marginBottom: 4, fontWeight: 700 }}>{d}</div>}
                {d && (byDay[d] || []).slice(0, 4).map((ev) => (
                  <button key={ev.id + d} data-testid={`cal-event-${ev.id}`} onClick={() => book(ev)} title={`${ev.title} — book`}
                    style={{
                      display: "block", width: "100%", textAlign: "left", border: 0, cursor: "pointer",
                      background: TYPE_COLOR[ev.type], color: "#fff", borderRadius: 6, padding: "3px 6px",
                      marginBottom: 3, fontSize: ".68rem", lineHeight: 1.2, overflow: "hidden",
                      whiteSpace: "nowrap", textOverflow: "ellipsis",
                    }}>
                    {ev.type === "session" ? `${ev.start} ` : ""}{ev.title}
                  </button>
                ))}
                {d && (byDay[d] || []).length > 4 && <div style={{ fontSize: ".65rem", color: "var(--muted)" }}>+{byDay[d].length - 4} more</div>}
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="sec pad" style={{ paddingTop: 0 }}>
        <div className="wrap">
          <h2 style={{ color: "#fff" }}>This month’s intensives & retreats</h2>
          {blocks.length ? (
            <div className="grid" style={{ marginTop: "1rem" }}>
              {blocks.map((ev) => (
                <article key={ev.id} className="tile" style={{ padding: "1.2rem", borderLeft: `4px solid ${TYPE_COLOR[ev.type]}` }}>
                  <span className="pill" style={{ background: TYPE_COLOR[ev.type], color: "#fff" }}>{ev.type === "retreat" ? "Retreat" : "Intensive"}</span>
                  <h3 style={{ color: "#fff", margin: ".6rem 0 .3rem" }}>{ev.title}</h3>
                  <p className="muted" style={{ fontSize: ".85rem", display: "flex", gap: "1rem", flexWrap: "wrap", margin: ".2rem 0 .6rem" }}>
                    <span style={{ display: "inline-flex", gap: ".3rem", alignItems: "center" }}><Clock size={13} /> {ev.date} → {ev.end_date} ({ev.days} days)</span>
                    <span style={{ display: "inline-flex", gap: ".3rem", alignItems: "center" }}><MapPin size={13} /> {ev.location}</span>
                  </p>
                  <button className="btn btn-gold" data-testid={`book-${ev.id}`} onClick={() => book(ev)}>Book · £{ev.price_gbp}</button>
                </article>
              ))}
            </div>
          ) : <p className="muted" style={{ marginTop: ".6rem" }}>No intensives or retreats this month — browse the daily live sessions above, or check other months.</p>}
        </div>
      </section>
      <Footer />
    </>
  );
}
